import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { type FC, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useSupabaseCallback } from "../../../../../server/supabase/hooks";
import {
  type CustomHeader,
  SupabaseCustomEmailHeaderService,
} from "../../server/supabase-custom-email-header-service";
import { type CustomEmailHeaderFormValues, customEmailHeaderSchema } from "../../types";

export const UpsertHeaderDialog: FC<{
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  customHeader?: CustomHeader;
}> = ({ open, onClose, onSave, customHeader }) => {
  const isEdit = Boolean(customHeader);
  const [saving, setSaving] = useState(false);

  const { handleSubmit, control, reset } = useForm<CustomEmailHeaderFormValues>({
    defaultValues: {
      display_name: customHeader?.display_name ?? "",
      header_name: customHeader?.header_name ?? "",
      header_value: customHeader?.header_value ?? "",
    },
    resolver: yupResolver(customEmailHeaderSchema),
    mode: "onChange",
  });

  const onSubmit = useSupabaseCallback(
    async ({ supabase, account_id }, values: CustomEmailHeaderFormValues): Promise<void> => {
      setSaving(true);

      const service = new SupabaseCustomEmailHeaderService(supabase);

      const { data, error } = customHeader
        ? await service.update(customHeader.id, {
            display_name: values.display_name,
            header_name: values.header_name,
            header_value: values.header_value,
          })
        : await service.insert({
            account_id,
            display_name: values.display_name,
            header_name: values.header_name,
            header_value: values.header_value,
          });

      setSaving(false);

      if (error || !data) {
        toast.error(
          `Something went wrong when ${isEdit ? "updating" : "adding"} header "${values.display_name}"`,
        );
        return;
      }

      toast.success(
        `Header "${values.display_name}" ${isEdit ? "updated" : "added"} successfully.`,
      );
      reset();
      onSave();
      onClose();
    },
    [customHeader, isEdit, onSave, onClose, reset],
  );

  const handleClose = (): void => {
    if (!saving) {
      reset();
      onClose();
    }
  };

  const formId = isEdit ? "edit-header-form" : "create-header-form";

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <form
        id={formId}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit(onSubmit)}
      >
        <DialogTitle>{isEdit ? "Edit" : "Add"} custom header</DialogTitle>
        <DialogContent>
          <Stack direction="column" spacing={3}>
            <DialogContentText>
              {!isEdit &&
                "Create a new custom header. You can use custom headers on email Communications \u2014 select \u201CMark as\u201D while editing a Communication to choose custom headers for that message."}
            </DialogContentText>
            <Controller
              name="display_name"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  margin="dense"
                  id="header-display-name"
                  label="Display name"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={field.value}
                  placeholder="High Priority"
                  onChange={field.onChange}
                  helperText={
                    error?.message || "Name displayed in the editor when flagging a Communication"
                  }
                  error={Boolean(error)}
                />
              )}
            />
            <Controller
              name="header_name"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  margin="dense"
                  id="header-name"
                  label="Header name"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={field.value}
                  placeholder="x-high-priority"
                  onChange={field.onChange}
                  helperText={error?.message || "Name of the header sent with the Communication"}
                  error={Boolean(error)}
                />
              )}
            />
            <Controller
              name="header_value"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  margin="dense"
                  id="header-value"
                  label="Header value"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={field.value}
                  placeholder="custom-value"
                  onChange={field.onChange}
                  helperText={error?.message || "Value for the header"}
                  error={Boolean(error)}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            data-analytics-id={`email-header-${isEdit ? "edit" : "add"}-dialog-cancel`}
            onClick={handleClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            data-analytics-id={`email-header-${isEdit ? "edit" : "add"}-dialog-save`}
            disabled={saving}
            type="submit"
            form={formId}
            variant="contained"
          >
            {saving ? <CircularProgress size={20} /> : isEdit ? "Update header" : "Add header"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
